import { SKILL_DEFAULT_BUFF_DURATION_MS, SKILL_SLOT_MAX } from "@/character/skills/constants";
import {
  createDefaultSkillBar,
  DEFAULT_ATTACK_SKILL_DAMAGE_MULT,
  normalizeSkillAttackPattern,
  normalizeSkillKind,
  type SkillBarState,
  type SkillDefinition,
} from "@/character/skills/types";
import { STAT_IDS, type StatId } from "@/game/model/character-stats";
import { normalizeGridSpec } from "@/character/grid";
import { SKILL_FRAME_COUNT, type SkillFrameLayout } from "@/character/skills/skill-frame-layout";
import {
  loadSkillBarFromIdb,
  saveSkillBarToIdb,
} from "@/character/skills/skill-idb";

function normalizeSkillDefinition(raw: unknown): SkillDefinition | null {
  if (!raw || typeof raw !== "object") return null;
  const s = raw as Partial<SkillDefinition>;
  if (
    typeof s.id !== "string" ||
    typeof s.name !== "string" ||
    typeof s.spritesheetDataUrl !== "string" ||
    typeof s.iconDataUrl !== "string" ||
    typeof s.createdAt !== "number" ||
    !s.grid ||
    typeof s.grid.frameWidth !== "number"
  ) {
    return null;
  }
  const kind = normalizeSkillKind(s.kind);
  const sourceSheetDataUrl =
    typeof s.sourceSheetDataUrl === "string" && s.sourceSheetDataUrl.length > 0
      ? s.sourceSheetDataUrl
      : undefined;
  let frameLayout: SkillFrameLayout | undefined;
  const rawLayout = s.frameLayout as SkillFrameLayout | undefined;
  if (rawLayout?.frames?.length === SKILL_FRAME_COUNT) {
    frameLayout = {
      frames: rawLayout.frames.map((f) => ({
        x: Number(f.x) || 0,
        y: Number(f.y) || 0,
        w: Math.max(8, Number(f.w) || 8),
        h: Math.max(8, Number(f.h) || 8),
      })),
    };
  }

  const damageMultiplier =
    typeof s.damageMultiplier === "number" && Number.isFinite(s.damageMultiplier)
      ? Math.min(5, Math.max(0.1, s.damageMultiplier))
      : DEFAULT_ATTACK_SKILL_DAMAGE_MULT;

  const buffStatBonus: Partial<Record<StatId, number>> = {};
  if (s.buffStatBonus && typeof s.buffStatBonus === "object") {
    for (const id of STAT_IDS) {
      const n = (s.buffStatBonus as Record<string, unknown>)[id];
      if (typeof n === "number" && Number.isFinite(n) && n > 0) {
        buffStatBonus[id] = Math.floor(n);
      }
    }
  }

  return {
    id: s.id,
    name: s.name,
    kind,
    durationMs: kind === "buff" ? SKILL_DEFAULT_BUFF_DURATION_MS : 0,
    spritesheetDataUrl: s.spritesheetDataUrl,
    grid: normalizeGridSpec(s.grid),
    iconDataUrl: s.iconDataUrl,
    frameRate:
      typeof s.frameRate === "number" && s.frameRate > 0 ? Math.floor(s.frameRate) : 14,
    createdAt: s.createdAt,
    sourceSheetDataUrl,
    frameLayout,
    cooldownCards:
      typeof s.cooldownCards === "number" && Number.isFinite(s.cooldownCards)
        ? Math.max(0, Math.floor(s.cooldownCards))
        : 0,
    damageMultiplier: kind === "attack" ? damageMultiplier : undefined,
    attackPattern:
      kind === "attack" ? normalizeSkillAttackPattern(s.attackPattern) : undefined,
    buffStatBonus: kind === "buff" && Object.keys(buffStatBonus).length > 0
      ? buffStatBonus
      : undefined,
    skillApCards:
      kind === "buff" &&
      typeof s.skillApCards === "number" &&
      Number.isFinite(s.skillApCards)
        ? Math.max(0, Math.floor(s.skillApCards))
        : undefined,
    buffDurationCards:
      kind === "buff" &&
      typeof s.buffDurationCards === "number" &&
      Number.isFinite(s.buffDurationCards)
        ? Math.max(0, Math.floor(s.buffDurationCards))
        : undefined,
    aoeRangeCards:
      kind === "attack" &&
      normalizeSkillAttackPattern(s.attackPattern) === "aoe" &&
      typeof s.aoeRangeCards === "number" &&
      Number.isFinite(s.aoeRangeCards)
        ? Math.max(0, Math.floor(s.aoeRangeCards))
        : undefined,
  };
}

export function normalizeSkillBar(raw: unknown): SkillBarState {
  const base = createDefaultSkillBar();
  if (!raw || typeof raw !== "object") return base;
  const parsed = raw as Partial<SkillBarState>;
  const library = Array.isArray(parsed.library)
    ? parsed.library
        .map(normalizeSkillDefinition)
        .filter((s): s is SkillDefinition => s != null)
        .slice(0, 64)
    : [];
  const slots = Array.from({ length: SKILL_SLOT_MAX }, (_, i) => {
    const id = Array.isArray(parsed.slots) ? parsed.slots[i] : null;
    if (typeof id !== "string") return null;
    return library.some((s) => s.id === id) ? id : null;
  });
  const rawAuto = (parsed as { slotAuto?: unknown }).slotAuto;
  const slotAuto = Array.from({ length: SKILL_SLOT_MAX }, (_, i) => {
    if (!Array.isArray(rawAuto)) return false;
    return rawAuto[i] === true;
  });
  const masterAuto = (parsed as { masterAuto?: unknown }).masterAuto === true;
  return { version: 3, slots, library, slotAuto, masterAuto };
}

export function newSkillId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `skill-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export async function loadSkillBar(userId: string): Promise<SkillBarState> {
  const raw = await loadSkillBarFromIdb(userId);
  if (!raw) return createDefaultSkillBar();
  return normalizeSkillBar(raw);
}

export async function saveSkillBar(userId: string, bar: SkillBarState): Promise<void> {
  await saveSkillBarToIdb(userId, normalizeSkillBar(bar));
  const { schedulePlayerCloudPush } = await import("@/cloud/player-cloud-scheduler");
  schedulePlayerCloudPush(userId, "skills");
}

export function skillById(bar: SkillBarState, id: string): SkillDefinition | undefined {
  return bar.library.find((s) => s.id === id);
}

export function assignSkillToSlot(bar: SkillBarState, slotIndex: number, skillId: string | null): void {
  if (slotIndex < 0 || slotIndex >= SKILL_SLOT_MAX) return;
  if (skillId && !skillById(bar, skillId)) return;
  bar.slots[slotIndex] = skillId;
}
